import { useMemo } from "react";
import { KalturaPlaylist } from "kaltura-typescript-client/api/types/KalturaPlaylist";
import Lobby from "../lobby/Lobby";
import { Player } from "@kaltura/mediaspace-shared-player";
import { HeroProps } from "../HeroProps";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import { baseUrl, cdnUrl, KalturaPlayer, translate } from "@kaltura/mediaspace-shared-utils";
import { Button } from "@kaltura/ds-react-components";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface PlaylistHeroProps extends HeroProps {
    playlist: KalturaPlaylist;
}

/**
 * Playlist Hero component - always renders a player
 */
export function PlaylistHero(props: PlaylistHeroProps) {
    const { playlist, entry, categoryId, playerConfig, onEntryChange, canEdit } = props;

    // player config media
    const media = useMemo(() => {
        return {
            playlistId: playlist.id,
            ks: playerConfig.ks as string,
        };
    }, [playlist, playerConfig]);

    // player play playlist options, pass `entry.id` as startAtEntryId so that playlist start playback from this entry
    // if not provided first entry in playlist will play
    const playlistOptions = useMemo(() => {
        const firstEntryId = playlist.playlistContent?.split(",")[0];
        if (firstEntryId !== entry?.id) {
            return { startAtEntryId: entry?.id };
        }
    }, [playlist, entry]);

    // edit playlist action
    const editAction = {
        name: "edit",
        component: Button,
        props: {
            component: "a",
            href: baseUrl + "/playlist/edit/" + playlist.id + (categoryId ? "/" + categoryId : ""),
            children: translate("Edit playlist"),
            analytics: {
                action: "Media Hero - Edit playlist",
                buttonType: ButtonClickAnalyticsType.EDIT,
            },
        },
    };

    // set the relevant action
    const actions = canEdit ? [editAction] : [];

    const handleReady = (player: any) => {
        // sync playlist and media page current entry
        player.addEventListener(
            KalturaPlayer.playlist.PlaylistEventType.PLAYLIST_ITEM_CHANGED,
            (e: { payload: { index: number } }) => {
                // on some cases(live + recorded), e.payload.activeItem.sources.id holds the value of the previous entry
                // before the change. get current entry from player instead
                //const entryId = e.payload.activeItem.sources.id;
                //const entryId = e.target.getMediaInfo().entryId; - unstable
                // temp fix until player fixes e.payload.activeItem.sources.id
                const entryIndex = e.payload.index;
                const entryId = player.playlist._mediaInfoList[entryIndex].entryId;

                window.history.pushState?.(
                    null,
                    "Playlist page",
                    baseUrl
                        + `/playlist/dedicated/`
                        + (categoryId ? `${categoryId}/` : "")
                        + `${playlist.id}/${entryId}`
                );

                // inform the media page
                onEntryChange?.(entryId);
            }
        );
    };

    return (
        <CommonHeroLayout>
            {entry && (
                <Player
                    media={media}
                    config={playerConfig}
                    options={playlistOptions}
                    className="kms-ds-media-page-player"
                    onReady={handleReady}
                />
            )}
            {!entry && (
                <Lobby
                    thumbnailUrl={cdnUrl + "/media/Thumbnail.png"}
                    statusLabel={translate("no content")}
                    title={playlist!.name}
                    actions={actions}
                />
            )}
        </CommonHeroLayout>
    );
}

export default PlaylistHero;
